// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// apiRefs — function references for the Convex experiment (#191).
//
// The Convex function code lives in the ROOT `convex/` dir (OUTSIDE src/) so
// Create React App never treats it as a TypeScript project (which would force a
// `typescript` dependency and break `npm start`/`npm run build` even in the
// default mongo mode). CRA's ModuleScopePlugin also forbids importing the
// generated `convex/_generated/api` from inside src/. So instead of importing
// the generated api, we build references by NAME with makeFunctionReference —
// fully equivalent for useQuery / ConvexHttpClient.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { makeFunctionReference } from "convex/server";

export const refs = {
  getUniqueLocations: makeFunctionReference("scheduler/offices:getUniqueLocations"),
  getOfficeMetaDataByLocation: makeFunctionReference(
    "scheduler/offices:getOfficeMetaDataByLocation"
  ),
  getUsers: makeFunctionReference("scheduler/users:getUsers"),
  getProjectsByCompany: makeFunctionReference("scheduler/activeQueue:getProjectsByCompany"),
  getProjectsByCompanyWithShared: makeFunctionReference(
    "scheduler/activeQueue:getProjectsByCompanyWithShared"
  ),
  getCandidatesByCompany: makeFunctionReference(
    "scheduler/candidates:getCandidatesByCompany"
  ),
  getAttributesByLocation: makeFunctionReference(
    "scheduler/attributes:getAttributesByLocation"
  ),
  getStoresByLocation: makeFunctionReference("scheduler/stores:getStoresByLocation"),
  getLaborCatalog: makeFunctionReference("scheduler/laborCatalog:getLaborCatalog"),
  getProjectById: makeFunctionReference("scheduler/activeQueue:getProjectById"),
  getProjectsByPayrollNoList: makeFunctionReference("scheduler/activeQueue:getProjectsByPayrollNoList"),
  getCandidatesByPayrollNoList: makeFunctionReference("scheduler/candidates:getCandidatesByPayrollNoList"),
  getUserByEmail: makeFunctionReference("scheduler/users:getUserByEmail"),
  getUserById: makeFunctionReference("scheduler/users:getUserById"),
  getSyncMeta: makeFunctionReference("sync:getSyncMeta"),
  // Compass data browser (#191) — read-only generic browse surface
  compassMirroredTables: makeFunctionReference("scheduler/compass:mirroredTables"),
  compassSearchableFields: makeFunctionReference("scheduler/compass:searchableFields"),
  compassGetByMongoId: makeFunctionReference("scheduler/compass:getByMongoId"),
  compassGetManyByMongoId: makeFunctionReference("scheduler/compass:getManyByMongoId"),
  compassBrowse: makeFunctionReference("scheduler/compass:browse"),
  // Jev window (2026-10-07) — live JevShadowLog viewer, "jev"-gated server-side.
  jevListRecent: makeFunctionReference("scheduler/jev:listRecent"),
  // Accuracy tiles over EVERY grade (an action that pages them — PRED-013).
  jevGradeSummary: makeFunctionReference("scheduler/jev:gradeSummary"),
  // "What Jev knows" tab (PRED-016 part 4): versions, checks, alerts, rollback / release.
  jevKnowledgeOverview: makeFunctionReference("scheduler/jevKnowledge:overview"),
  // FAV Inbox (#630): the office's open FAV bookings, Approve, Defer.
  favInboxList: makeFunctionReference("scheduler/favInbox:listForLocation"),
  // Crew cells (#695): each crew's LTR survey pass rate, 4 weeks + year.
  crewLtrForCrews: makeFunctionReference("scheduler/crewScores:ltrForCrews"),
  favInboxApprove: makeFunctionReference("scheduler/favInbox:approve"),
  favInboxDefer: makeFunctionReference("scheduler/favInbox:defer"),
  // Notifications "Clear" / "Clear all" (Leland 2026-10-26) — replaces Unschedule / Approve / Defer.
  favInboxClear: makeFunctionReference("scheduler/favInbox:clear"),
  // FAV 2.0 log (#652): the Jev window's FAV 2.0 tab; an office's advice-only proposals.
  fav2LogList: makeFunctionReference("scheduler/fav2Log:listRecent"),
  fav2Advice: makeFunctionReference("scheduler/fav2Log:adviceForLocation"),
  // #755: jobs FAV 2.0 couldn't schedule (no crew, no date) — the red box + ⓘ on their tile.
  fav2Skips: makeFunctionReference("scheduler/fav2Log:skipsForLocation"),
  fav2Rerun: makeFunctionReference("scheduler/fav2Rerun:request"), // #787 red box Re-run
  jevKnowledgeRequestRollback: makeFunctionReference("scheduler/jevKnowledge:requestRollback"),
  jevKnowledgeRequestRelease: makeFunctionReference("scheduler/jevKnowledge:requestRelease"),
  compassStats: makeFunctionReference("sync:stats"),
  // mutations (write-path dual-run)
  setScheduleById: makeFunctionReference("scheduler/activeQueue:setScheduleById"),
  setSuppressionById: makeFunctionReference("scheduler/activeQueue:setSuppressionById"),
  setAttributeById: makeFunctionReference("scheduler/attributes:setAttributeById"),
  createNewAttribute: makeFunctionReference("scheduler/attributes:createNewAttribute"),
  setUserById: makeFunctionReference("scheduler/users:setUserById"),
  createNewUser: makeFunctionReference("scheduler/users:createNewUser"),
  // #344 — createLogEntry removed: the Convex function it pointed at wrote to a
  // `logs` table that never existed in the schema, and nothing invoked this ref
  // (modelCx's createLogEntry always calls the mutation Lambda). Logs are not
  // mirrored to Convex by design — see the note in convex/sync.ts.
  // parity comparison log
  recordComparison: makeFunctionReference("scheduler/comparisons:recordComparison"),
  // Time Machine (#114/#191) — point-in-time reconstruction + EventLog history.
  // These ALWAYS read PROD inside Convex (source hardcoded to "prod", #121).
  getStateAtTimestamp: makeFunctionReference("scheduler/timeMachine:getStateAtTimestamp"),
  getStateAndEventsForDate: makeFunctionReference("scheduler/timeMachine:getStateAndEventsForDate"),
  getEventLogByDocumentId: makeFunctionReference("scheduler/timeMachine:getEventLogByDocumentId"),
  getEventLogForCrew: makeFunctionReference("scheduler/timeMachine:getEventLogForCrew"),
  getEventLogForAttributeCell: makeFunctionReference("scheduler/timeMachine:getEventLogForAttributeCell"),
  // Ticket Master (#276) — server-side FileMaker read path for lanes ②③
  // (Schedule [DATA] + TurboQ). Read-only, requireUser-gated.
  ticketMasterReadFileMakerLanes: makeFunctionReference("scheduler/ticketMaster:readFileMakerLanes"),
  // Ticket Master (#276) — 🤖 Claude conclusion (opus-4-8; free-form prose).
  ticketMasterConclude: makeFunctionReference("scheduler/ticketMaster:conclude"),
  fav2ForJob: makeFunctionReference("scheduler/fav2Log:forJob"), // Ticket Master lane ④ FAV calls
};
